/**
 * Dynamically load Leaflet's CSS + JS the first time the map is opened.
 *
 * - Both the <link> and <script> are appended to <head> (NOT inside the
 *   shadow root) because Leaflet's CSS positions tile elements with rules
 *   that need to apply on the same DOM tree as the map, and the map itself
 *   lives in light DOM (it's mounted into a div outside the shadow root).
 *
 * - The promise is cached; subsequent calls reuse the same load.
 */

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

const LEAFLET_VERSION = '1.9.4';
const CSS_URL = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const JS_URL  = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

let pendingPromise = null;
let googleMapsPromise = null;

/**
 * Load Leaflet on demand.
 * @returns {Promise<typeof import('leaflet')>} resolves to window.L
 */
export function loadLeaflet() {
  // Ensure window.L exists and is actually Leaflet (not an HTML element with id="L")
  const isLeafletLoaded = () => window.L && typeof window.L.map === 'function';

  if (isLeafletLoaded()) return Promise.resolve(window.L);
  if (pendingPromise) return pendingPromise;

  pendingPromise = new Promise((resolve, reject) => {
    // CSS: don't bother awaiting it — Leaflet works without CSS but tiles
    // look broken until it's parsed; injecting it before the JS tag means
    // it almost always lands first anyway.
    if (!document.querySelector(`link[data-leaflet-css]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CSS_URL;
      link.crossOrigin = '';
      link.dataset.leafletCss = '';
      document.head.appendChild(link);
    }

    // JS
    if (isLeafletLoaded()) return resolve(window.L);
    const existing = document.querySelector(`script[data-leaflet-js]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', () => reject(new Error('Leaflet failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.src = JS_URL;
    script.crossOrigin = '';
    script.async = true;
    script.dataset.leafletJs = '';
    script.addEventListener('load', () => {
      if (isLeafletLoaded()) resolve(window.L);
      else reject(new Error('Leaflet loaded but window.L is undefined'));
    });
    script.addEventListener('error', () => {
      // Allow a retry on next call by clearing the cache.
      pendingPromise = null;
      reject(new Error('Leaflet failed to load. Check your network connection.'));
    });
    document.head.appendChild(script);
  });

  return pendingPromise;
}

/** Drop the cached promise so a retry can re-attempt loading. */
export function resetLeafletLoader() {
  pendingPromise = null;
}

/**
 * Inject Leaflet's CSS into a shadow root so styles apply to a map mounted
 * inside the shadow tree. Idempotent.
 */
export function injectLeafletCSSInto(root) {
  if (!root) return;
  if (root.querySelector?.('link[data-leaflet-css-shadow]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = CSS_URL;
  link.crossOrigin = '';
  link.dataset.leafletCssShadow = '';
  root.appendChild(link);
}

let setOptionsCalled = false;

/**
 * Dynamically load Google Maps JavaScript API using the official loader.
 * @param {string} apiKey - The Google Maps API key.
 * @returns {Promise<any>} resolves to window.google
 */
export function loadGoogleMaps(apiKey) {
  const isLoaded = window.google && window.google.maps && typeof window.google.maps.Map === 'function';
  if (isLoaded) {
    return Promise.resolve(window.google);
  }
  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = (async () => {
    try {
      if (apiKey && !setOptionsCalled) {
        setOptions({
          key: apiKey,
        });
        setOptionsCalled = true;
      }

      await Promise.all([
        importLibrary('maps'),
        importLibrary('marker'),
      ]);

      return window.google;
    } catch (error) {
      googleMapsPromise = null;
      throw error;
    }
  })();

  return googleMapsPromise;
}
